import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import './Technologies.css';
import healthcareImage from '../assets/Innovation-1.jpg';
import rpa from '../assets/RPA-automation-what-we-do.jpg';
import bigdata from '../assets/Business_Data_Analytics.jpg';
import edi from '../assets/EDI.jpg';
import genai from '../assets/GenAI.jpg';
import aiml from '../assets/AIML.jpg';
import mlops from '../assets/MLOps.png';
import nlp from '../assets/NLP.jpg';
import footerBackground from '../assets/footer-background.jpg';
import Footer from './footer';

const currentYear = new Date().getFullYear();

const Technologies = () => {
    const { id } = useParams(); // Get the id from the URL
    const sectionRefs = useRef({}); // Refs for scrolling

    // Scroll to the specific technology section if an ID is provided
    useEffect(() => {
        if (id && sectionRefs.current[id]) {
            sectionRefs.current[id].scrollIntoView({ behavior: 'smooth' });
        }
    }, [id]);


    // Define all technologies
    const technologyContents = [
        {
            id: 'rpa',
            title: "Robotic Process Automation (RPA)",
            description: (
                <>
                    Discover the transformative power of RPA in revolutionizing your business operations. Our
                    <strong> Robotic Process Automation</strong> solutions specialize in automating mundane, repetitive
                    tasks, freeing up your workforce to focus on strategic and creative initiatives.
                    Whether it's invoice processing, customer onboarding, or inventory management, we develop scalable RPA
                    workflows that seamlessly integrate into your existing systems.
                    <br /><br />
                    Our expert team ensures that your automation journey is smooth, efficient, and aligned with your
                    business goals. With reduced processing time, minimized errors, and enhanced compliance,
                    <strong> RPA</strong> is your gateway to operational excellence. Let us help you embrace the
                    future of automation!
                </>
            ),
            image: rpa
        },
        {
            id: 'bigdata',
            title: "Big Data & Cloud Analytics",
            description: (
                <>
                    In today’s digital era, <strong>data</strong> is the backbone of every successful organization.
                    Our <strong>Big Data and Cloud Analytics</strong> services are designed to help you aggregate,
                    analyze, and visualize vast volumes of data with unparalleled accuracy and efficiency.
                    <br /><br />
                    We specialize in crafting scalable cloud solutions, enabling seamless data migration and
                    management on platforms like <strong>AWS</strong>, <strong>Azure</strong>, and <strong>Google Cloud</strong>.
                    Whether it’s building predictive models, optimizing business intelligence tools, or implementing
                    real-time analytics, we provide end-to-end support tailored to your unique needs.
                    <br /><br />
                    With our expertise, transform raw data into valuable insights that drive smarter, faster
                    decision-making. Stay ahead of the competition by unlocking the true potential of your data.
                </>
            ),
            image: bigdata
        },
        {
            id: 'edi',
            title: "EDI X12 Converter as a Service",
            description: (
                <>
                    <strong>EDI (Electronic Data Interchange)</strong> is the lifeline of seamless communication in
                    industries like healthcare, supply chain, and logistics. Our <strong>EDI X12 Converter</strong>
                    services simplify the processing and exchange of critical data such as healthcare claims,
                    invoices, and shipping notices.
                    <br /><br />
                    We offer a robust, scalable, and secure platform to translate, validate, and transmit EDI
                    documents effortlessly. Our solutions ensure complete compliance with industry standards while
                    optimizing data accuracy and reducing processing times. Whether you're a small business or a
                    large enterprise, our EDI services are designed to meet your unique requirements. Simplify your
                    workflows and enhance operational efficiency with us!
                </>
            ),
            image: edi
        },
        {
            id: 'aiml',
            title: "AIML Solutions",
            description: (
                <>
                    Experience the transformative impact of <strong>AI and ML</strong> in solving real-world business 
                    challenges. Our <strong>AIML solutions</strong> span a wide range of applications, including 
                    predictive modeling, recommendation systems, fraud detection, and process automation.
                    <br /><br />
                    From data preprocessing to model deployment, we handle every aspect of the AI lifecycle with 
                    precision and expertise. With cutting-edge algorithms and a keen understanding of your business 
                    needs, we deliver tailor-made solutions that provide actionable intelligence and measurable results. 
                    Let us help you redefine innovation and drive success with <strong>AIML</strong>.
                </>
            ),
            image: aiml
        },
        {
            id: 'genai',
            title: "GenAI Application Development",
            description: (
                <>
                    <strong>Generative AI</strong> is reshaping how we interact with technology, and we are at the 
                    forefront of this transformation. Our expertise in frameworks like <strong>LangChain</strong>, 
                    <strong>LangGraph</strong>, and <strong>LangSmith</strong> enables us to create powerful AI-driven 
                    applications capable of natural language understanding, creative content generation, and workflow automation.
                    <br /><br />
                    From building intelligent chatbots to designing AI systems that generate highly personalized user 
                    experiences, our <strong>GenAI solutions</strong> are tailored to your business needs. Whether it's 
                    enhancing customer engagement or simplifying complex processes, we bring your ideas to life with 
                    <strong>GenAI</strong>.
                </>
            ),
            image: genai
        },
        {
            id: 'nlp',
            title: "Natural Language Models",
            description: (
                <>
                    <strong>Natural Language Models (NLMs)</strong> are at the heart of modern AI applications, enabling 
                    human-like interactions and precise understanding of text and speech. We specialize in developing 
                    and deploying <strong>NLM solutions</strong> tailored to your specific needs.
                    <br /><br />
                    From creating conversational agents and chatbots to enabling automated document summarization and 
                    sentiment analysis, our solutions enhance user engagement and operational efficiency. Whether it’s 
                    transforming customer service or improving workflow automation, our <strong>NLM expertise</strong> 
                    ensures meaningful impact and lasting results.
                </>
            ),
            image: nlp
        },
        {
            id: 'mlops',
            title: "MLOps for Model Monitoring",
            description: (
                <>
                    <strong>MLOps (Machine Learning Operations)</strong> is critical for the sustained success of AI 
                    systems. Our <strong>MLOps solutions</strong> offer robust frameworks for monitoring, deploying, 
                    and governing both traditional and large language models (LLMs).
                    <br /><br />
                    We provide end-to-end support, from setting up model pipelines to ensuring compliance with ethical 
                    and regulatory standards. With advanced monitoring tools, we track model performance, detect drifts, 
                    and implement governance protocols that align with your business objectives. Unlock the full potential 
                    of your AI investments with our <strong>MLOps expertise</strong>.
                </>
            ),
            image: mlops
        },
    ];

    return (
        <div className="technologies-container">
            {technologyContents.map((item, index) => (
                <Card
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    description={item.description}
                    image={item.image}
                    reverse={index % 2 === 0} // Alternate layout logic
                />
            ))}
            <Footer />
        </div>
    );
};

const Card = ({ id, title, description, image, reverse }) => {
    const ref = useRef(null); // Ref to detect visibility
    const isInView = useInView(ref, { once: true }); // Trigger animations only once

    return (
        <motion.div
            id={id}
            ref={ref}
            className={`technology-card ${reverse ? "reverse" : ""}`}
            initial={{ opacity: 0, x: reverse ? -100 : 100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
        >
            {/* Left side (Image) */}
            <motion.div
                className="technology-card-left"
                initial={{ opacity: 0, x: reverse ? -100 : 100 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
            >
                <img src={image} alt={title} />
            </motion.div>

            {/* Right side (Content) */}
            <motion.div
                className="technology-card-right"
                initial={{ opacity: 0, x: reverse ? 100 : -100 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                <h2>{title}</h2>
                <p>{description}</p>
            </motion.div>
        </motion.div>
    );
};

export default Technologies;
